import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useGetResourceByIdQuery, useUpdateResourceMutation } from './resourcesApi';

import { asRubricContent } from '@/shared/types/resource';
import type { UpdateResourceRequest } from '@/shared/types/resource';
import type { RubricContent } from '@/shared/types/rubric';
import { useObjectPermissionState } from '@/shared/hooks/useCanEdit';
import { useAppSelector } from '@/shared/hooks/redux';
import { PageSpinner } from '@/shared/components/PageSpinner';
import { EditableText } from '@/shared/components/EditableText';
import RubricGrid from '@/features/rubrics/components/RubricGrid';
import HolisticEditor from '@/features/rubrics/components/HolisticEditor';
import SinglePointEditor from '@/features/rubrics/components/SinglePointEditor';

const AUTOSAVE_DELAY = 900;

function typeLabel(content: RubricContent | null): string {
  if (!content) return 'Empty';
  switch (content.rubricType) {
    case 'analytic':
      return 'Analytic';
    case 'holistic':
      return 'Holistic';
    case 'single-point':
      return 'Single-Point';
    default:
      return 'Unknown';
  }
}

function typeColor(content: RubricContent | null): 'primary' | 'secondary' | 'success' | 'default' {
  if (!content) return 'default';
  switch (content.rubricType) {
    case 'analytic':
      return 'primary';
    case 'holistic':
      return 'secondary';
    case 'single-point':
      return 'success';
    default:
      return 'default';
  }
}

function backLink(resource: {
  parentLessonId: string | null;
  parentUnitId: string | null;
  parentCourseId: string | null;
  courseId: string;
}): string {
  if (resource.parentLessonId) return `/curriculum/lessons/${resource.parentLessonId}`;
  if (resource.parentUnitId) return `/curriculum/units/${resource.parentUnitId}`;
  if (resource.parentCourseId) return `/curriculum/courses/${resource.parentCourseId}`;
  if (resource.courseId) return `/curriculum/courses/${resource.courseId}`;
  return '/curriculum';
}

const ResourceRubricEditorPage: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const navigate = useNavigate();
  const { user, initializing: authLoading } = useAppSelector((state) => state.auth);

  const {
    data: resource,
    isLoading,
    error,
  } = useGetResourceByIdQuery(resourceId!, {
    skip: !resourceId || authLoading,
  });
  const isOwner = Boolean(resource && user && resource.ownerId === user.id);
  const resourcePermission = useObjectPermissionState('resource', resourceId, 'editor', {
    fallbackAllowed: isOwner,
  });
  const canEdit = resourcePermission.isAllowed;
  const [updateResource] = useUpdateResourceMutation();

  const rubricContent = useMemo(() => (resource ? asRubricContent(resource) : null), [resource]);
  const sourceRubricId = rubricContent?.sourceRubricId;

  const [localTitle, setLocalTitle] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const [localContent, setLocalContent] = useState<RubricContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const initializedRef = useRef(false);
  const changeCountRef = useRef(0);

  useEffect(() => {
    if (!resource) return;
    if (initializedRef.current && dirty) return;
    setLocalTitle(resource.title);
    setLocalDescription(resource.description ?? '');
    setLocalContent(rubricContent?.rubric ?? null);
    initializedRef.current = true;
  }, [resource, rubricContent, dirty]);

  useEffect(() => {
    if (!canEdit || !dirty || !resourceId || !resource) return;
    const countAtSchedule = changeCountRef.current;
    const timer = setTimeout(() => {
      const payload: UpdateResourceRequest = { id: resourceId };
      let hasChange = false;
      if (localTitle !== resource.title) {
        payload.title = localTitle;
        hasChange = true;
      }
      if (localDescription !== (resource.description ?? '')) {
        payload.description = localDescription || null;
        hasChange = true;
      }
      if (localContent !== rubricContent?.rubric) {
        payload.content = { ...(rubricContent ?? {}), rubric: localContent, sourceRubricId };
        hasChange = true;
      }
      if (hasChange) {
        payload.version = (resource.version ?? 1) + 1;
        updateResource(payload)
          .unwrap()
          .then(() => {
            if (changeCountRef.current === countAtSchedule) {
              setDirty(false);
            }
          })
          .catch(() => {
            /* keep dirty — retry on next change */
          });
      } else {
        setDirty(false);
      }
    }, AUTOSAVE_DELAY);
    return () => clearTimeout(timer);
  }, [
    dirty,
    resourceId,
    localTitle,
    localDescription,
    localContent,
    resource,
    rubricContent,
    sourceRubricId,
    canEdit,
    updateResource,
  ]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const handleTitleChange = useCallback(async (title: string) => {
    setLocalTitle(title);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  const handleDescriptionChange = useCallback(async (desc: string) => {
    setLocalDescription(desc);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  const handleContentChange = useCallback((content: RubricContent) => {
    setLocalContent(content);
    changeCountRef.current++;
    setDirty(true);
  }, []);

  if (isLoading) return <PageSpinner />;
  if (error || !resource) {
    return (
      <Box sx={{ py: 6, px: { xs: 2, md: 4 }, textAlign: 'center' }}>
        <Alert severity="error">
          Rubric resource not found or you don't have permission to view it.
        </Alert>
      </Box>
    );
  }

  if (resource.resourceKind !== 'rubric') {
    return (
      <Box sx={{ py: 6, px: { xs: 2, md: 4 }, textAlign: 'center' }}>
        <Alert severity="error">This resource is not a rubric.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Chip
              label={typeLabel(localContent)}
              color={typeColor(localContent)}
              size="small"
              variant="outlined"
            />
          </Box>
          <EditableText
            value={localTitle}
            onSave={handleTitleChange}
            canEdit={canEdit}
            typographyProps={{ variant: 'h2', component: 'h1' }}
            textFieldProps={{
              sx: {
                '& .MuiInputBase-input': {
                  fontSize: '2.125rem',
                  fontWeight: 300,
                  lineHeight: 1.235,
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(backLink(resource))}
          >
            Back
          </Button>
        </Box>
        <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>
          <EditableText
            value={localDescription}
            onSave={handleDescriptionChange}
            canEdit={canEdit}
            allowEmpty
            multiline
            typographyProps={{ variant: 'subtitle1', color: 'text.secondary' }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {localContent?.rubricType === 'analytic' && (
        <RubricGrid
          content={localContent}
          canEdit={canEdit}
          ownerId={resource.ownerId}
          onChange={handleContentChange}
        />
      )}

      {localContent?.rubricType === 'holistic' && (
        <HolisticEditor content={localContent} canEdit={canEdit} onChange={handleContentChange} />
      )}

      {localContent?.rubricType === 'single-point' && (
        <SinglePointEditor
          content={localContent}
          canEdit={canEdit}
          ownerId={resource.ownerId}
          onChange={handleContentChange}
        />
      )}
    </Box>
  );
};

export default ResourceRubricEditorPage;
